import React from "react";
import {
  Avatar,
  Checkbox,
  IconButton,
  ListItemAvatar,
  ListItemText,
  Tooltip,
  Typography,
} from "@mui/material";
import ListItemButton from "@mui/material/ListItemButton";
import JumboDdMenu from "@jumbo/components/JumboDdMenu";
import useJumboAuth from "@jumbo/hooks/useJumboAuth";
import { useSmartNavigation } from "app/utils/navigationHelper";
import ScrapingStatusBadge from "app/pages/components/ScrapingStatusBadge";

import HomeIcon from "@mui/icons-material/Home";
import InfoIcon from '@mui/icons-material/Info';
import PersonIcon from '@mui/icons-material/Person';

const ProcessItem = ({ 
  processo, 
  isSelected = false, 
  isPending = false, 
  isConfirmed = false, 
  onSelect = () => {}, 
  isUpdating = false,
  showSelectionControls = false 
}) => {
  const { authUser } = useJumboAuth();
  const { navigateToProcesso } = useSmartNavigation();

  const handleNavigation = () => {
    navigateToProcesso(processo.id);
  };

  return (
    <React.Fragment>
      <ListItemButton
        component={"li"}
        sx={{
          p: (theme) => theme.spacing(1, 3),
          borderBottom: 1,
          borderBottomColor: "divider",
          backgroundColor: (() => {
            if (isConfirmed && !isPending) return "rgba(76, 175, 80, 0.08)";
            if (isPending) return "rgba(255, 152, 0, 0.05)";
            if (processo?.is_processo_de_terceiro) return "rgba(255, 193, 7, 0.1)";
            return "transparent";
          })(),
          "&:hover": {
            backgroundColor: (() => {
              if (isConfirmed && !isPending) return "rgba(76, 175, 80, 0.12)";
              if (isPending) return "rgba(255, 152, 0, 0.08)";
              if (processo?.is_processo_de_terceiro) return "rgba(255, 193, 7, 0.2)";
              return "action.hover";
            })(),
          },
          "&:last-child": {
            borderBottomColor: "transparent",
          },
          borderLeft: (() => {
            if (isConfirmed && !isPending) return '4px solid #4CAF50';
            if (isPending) return '4px solid #ff9800';
            return '4px solid transparent';
          })(),
        }}
      >
        {/* Checkbox de seleção */}
        {showSelectionControls && (
          <ListItemText
            sx={{
              width: "5%",
              display: "flex",
              flexDirection: "column",
              alignItems: "center",
              justifyContent: "center",
            }}
            primary={
              <Checkbox
                checked={isSelected}
                onChange={onSelect}
                size="small"
                disabled={isUpdating}
              />
            }
            secondary={
              <>
                {isPending && (
                  <Typography 
                    variant="caption"
                    sx={{ 
                      color: '#ff9800',
                      fontSize: '0.7em',
                      fontWeight: 'bold'
                    }}
                  >
                    PENDENTE
                  </Typography>
                )}
                {(isSelected && !isPending) && (
                  <Typography 
                    variant="caption"
                    sx={{ 
                      color: '#4CAF50',
                      fontSize: '0.7em',
                      fontWeight: 'bold'
                    }}
                  >
                    CONFIRMADO
                  </Typography>
                )}
                {isUpdating && (
                  <Typography 
                    variant="caption"
                    sx={{ 
                      color: '#2196F3',
                      fontSize: '0.7em',
                      fontWeight: 'bold'
                    }}
                  >
                    ATUALIZANDO
                  </Typography>
                )}
              </>
            }
          />
        )}
        <ListItemAvatar>
          <Avatar
            alt={processo?.marca?.nome}
            src={`${process.env.REACT_APP_BACKEND_URL}/public/images/marcas/${processo.numero}.jpg`}
          />
        </ListItemAvatar>
        <ListItemText
          sx={{
            width: showSelectionControls ? "8%" : "10%",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
          }}
          primary={
            <Typography variant="h8" mb={0.5} fontWeight={600}>
              {processo?.is_processo_de_terceiro ? (
                <Tooltip title="Processo de Terceiro">
                  <IconButton>
                    <PersonIcon color="warning" />
                  </IconButton>
                </Tooltip>
              ) : !String(processo?.procurador)
                  .toLowerCase()
                  .includes("vivian barbosa") ? (
                <Tooltip title="Processo em Acompanhamento">
                  <IconButton>
                    <InfoIcon color="info" />
                  </IconButton>
                </Tooltip>
              ) : (
                <Tooltip title="Processo Próprio da Bunker">
                  <IconButton>
                    <HomeIcon color="success" />
                  </IconButton>
                </Tooltip>
              )}
            </Typography>
          }
          secondary={
            <Typography
              variant={"body1"}
              fontSize={12}
              color={"text.secondary"}
              textAlign="center"
            >
              {processo?.is_processo_de_terceiro 
                ? "Terceiro" 
                : !String(processo?.procurador).toLowerCase().includes("vivian barbosa")
                ? "Acompanhamento"
                : "Próprio"}
            </Typography>
          }
        />
        <ListItemText
          sx={{ width: showSelectionControls ? "8%" : "10%" }}
          primary={
            <Typography variant="h8" mb={0.5} fontWeight={600}>
              {processo?.numero ?? "- - -"}
            </Typography>
          }
          secondary={
            <Typography
              variant={"body1"}
              fontSize={14}
              color={"text.secondary"}
            >
              Nº Processo
            </Typography>
          }
        />
        <ListItemText
          sx={{ width: showSelectionControls ? "22%" : "25%" }}
          primary={
            <Typography variant="h8" mb={0.5} fontWeight={600}>
              {processo?.marca[0]?.nome ?? "-"}
            </Typography>
          }
          secondary={
            <Typography
              variant={"body1"}
              fontSize={14}
              color={"text.secondary"}
            >
              Marca
            </Typography>
          }
        />
        <ListItemText
          sx={{ width: showSelectionControls ? "22%" : "25%" }}
          primary={
            <Typography variant="h8" mb={0.5} fontWeight={600}>
              {processo?.revista_rpi?.numero ?? "-"}
            </Typography>
          }
          secondary={
            <Typography
              variant={"body1"}
              fontSize={14}
              color={"text.secondary"}
            >
              RPI
            </Typography>
          }
        />
        <ListItemText
          sx={{ 
            width: showSelectionControls ? "12%" : "15%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center"
          }}
          primary={
            <ScrapingStatusBadge
              is_scraping_completo={processo?.is_scraping_completo}
              fonte_scraping={processo?.fonte_scraping}
              data_ultimo_scraping={processo?.data_ultimo_scraping}
              size="small"
              showTooltip={true}
            />
          }
        />
        <ListItemText>
          <JumboDdMenu
            menuItems={[
              {
                title: "Visualizar",
                slug: "visualizar",
                onClickItem: handleNavigation,
              },
              ...(authUser?.role !== 'CLIENTE' ? [
                {
                  title: "Editar",
                  slug: "editar",
                  onClickItem: handleNavigation,
                }
              ] : [])
            ]}
          />
        </ListItemText>
      </ListItemButton>
    </React.Fragment>
  );
};

export default React.memo(ProcessItem);
